import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SplashScreen from "@/components/SplashScreen";
import Layout from "@/components/Layout";
import { Loading } from "@/components/Common";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import LivePositions from "@/pages/LivePositions";
import Performance from "@/pages/Performance";
import Trades from "@/pages/Trades";
import TradeDetail from "@/pages/TradeDetail";
import Calendar from "@/pages/Calendar";
import TradingDNA from "@/pages/TradingDNA";
import SessionAnalysis from "@/pages/SessionAnalysis";
import RiskMonitor from "@/pages/RiskMonitor";
import AIAnalyst from "@/pages/AIAnalyst";
import DeluxeScore from "@/pages/DeluxeScore";
import Settings from "@/pages/Settings";
import ComingSoon from "@/pages/ComingSoon";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading label="Verifica sessione…" />;
  if (user === null) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/positions" element={<LivePositions />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/trades" element={<Trades />} />
        <Route path="/trades/:id" element={<TradeDetail />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/dna" element={<TradingDNA />} />
        <Route path="/sessions" element={<SessionAnalysis />} />
        <Route path="/risk" element={<RiskMonitor />} />
        <Route path="/ai" element={<AIAnalyst />} />
        <Route path="/score" element={<DeluxeScore />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/edge" element={<ComingSoon title="Edge Finder" description="Analisi automatica delle combinazioni storiche (Giorno+Ora, Sessione+Direzione, Setup+Sessione…) con numero di campioni, win rate, expectancy e profit factor. Solo vantaggio storico, mai edge garantito." dataPoints={["Giorno + Ora", "Giorno + Sessione", "Sessione + Direzione", "Setup + Sessione", "Setup + Direzione", "Ora + Direzione"]} />} />
        <Route path="/behavior" element={<ComingSoon title="Behavior Analysis" description="Osservazioni statistiche su overtrading, revenge trading, loss escalation, win overconfidence e performance dopo perdite/vittorie. Nessuna diagnosi psicologica." dataPoints={["Overtrading", "Revenge Trading", "Loss Escalation", "Win Overconfidence", "After-Loss Performance", "After-Win Performance"]} />} />
        <Route path="/setups" element={<ComingSoon title="Setup Analytics" description="Statistiche complete per ogni setup (Breakout, Pullback, Reversal…) con incroci Setup+Session, Setup+Direction, Setup+Day." dataPoints={["Breakout", "Pullback", "Reversal", "Continuation", "Liquidity Sweep", "Support/Resistance"]} />} />
        <Route path="/whatif" element={<ComingSoon title="What If?" description="Simulazioni storiche: cosa sarebbe successo evitando certi orari, giorni, sessioni o setup. Historical simulation — non è una previsione futura." dataPoints={["Evita trade dopo le 17:00", "Stop dopo 2 perdite", "Escludi il lunedì", "Solo sessione New York", "Escludi setup peggiore"]} />} />
        <Route path="/report" element={<ComingSoon title="Monthly Report" description="Report mensile automatico con starting/ending balance, net P&L, return %, drawdown, expectancy, best/worst, analisi comportamentale. Pronto per esportazione PDF." dataPoints={["Net P&L", "Return %", "Profit Factor", "Max Drawdown", "Best/Worst", "PDF Export"]} />} />
        <Route path="/records" element={<ComingSoon title="Personal Records" description="I tuoi record personali: best/worst trade, best/worst day & month, streak più lunghe, simbolo più profittevole, R più alto, recupero più rapido." dataPoints={["Best Trade", "Best Month", "Longest Streak", "Highest R", "Most Profitable Symbol"]} />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("dlx_splash_seen"));
  useEffect(() => {
    if (!showSplash) sessionStorage.setItem("dlx_splash_seen", "1");
  }, [showSplash]);

  return (
    <div className="App min-h-screen bg-[#050505]">
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0F0F12", border: "1px solid rgba(212,175,55,0.2)", color: "#fff" } }} />
      <AnimatePresence>
        {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      </AnimatePresence>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
